import React, { useState, useRef, useEffect } from 'react';
import { X, Check, ZoomIn } from 'lucide-react';

const ImageAdjustModal = ({ imageUrl, initialPosition, initialZoom, isCircle, onSave, onClose }) => {
    const [zoom, setZoom] = useState(initialZoom || 1);
    const [position, setPosition] = useState(() => {
        const parts = (initialPosition || '50% 50%').split(' ');
        return {
            x: parseFloat(parts[0]) || 50,
            y: parseFloat(parts[1]) || 50
        };
    });

    const [imageDims, setImageDims] = useState(null);

    useEffect(() => {
        if (!imageUrl) return;
        const img = new globalThis.Image();
        img.src = imageUrl;
        img.onload = () => {
            setImageDims({
                width: img.naturalWidth,
                height: img.naturalHeight
            });
        };
    }, [imageUrl]);

    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, pos: { x: 50, y: 50 } });
    const containerRef = useRef(null);

    const handleStart = (e) => {
        setIsDragging(true);
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        dragStart.current = { 
            x: clientX, 
            y: clientY, 
            pos: { ...position } 
        };
    };

    const handleMove = (e) => {
        if (!isDragging) return;
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        const deltaX = clientX - dragStart.current.x;
        const deltaY = clientY - dragStart.current.y;

        const rect = containerRef.current.getBoundingClientRect();
        
        // Sensitivity logic:
        // We want the image to move WITH the mouse.
        // If mouse moves 10px RIGHT, focal point % should DECREASE (showing more of the left side).
        const sensitivity = 100 / rect.width;
        
        // We divide by zoom because at higher zoom, a 1% change moves the image more pixels.
        const nextX = dragStart.current.pos.x - (deltaX * sensitivity) / Math.max(1, zoom);
        const nextY = dragStart.current.pos.y - (deltaY * sensitivity) / Math.max(1, zoom);

        setPosition({
            x: Math.max(0, Math.min(100, nextX)),
            y: Math.max(0, Math.min(100, nextY))
        });
    };

    const handleEnd = () => {
        setIsDragging(false);
    };

    const handleMoveRef = useRef(handleMove);
    useEffect(() => {
        handleMoveRef.current = handleMove;
    });

    useEffect(() => {
        const onMove = (e) => handleMoveRef.current(e);
        if (isDragging) {
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend', handleEnd);
        }
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [isDragging]);

    const handleSaveClick = () => {
        onSave(`${position.x.toFixed(2)}% ${position.y.toFixed(2)}%`, zoom);
    };

    const imageAspectRatio = imageDims ? imageDims.width / imageDims.height : 1;
    const cropAspectRatio = isCircle ? 1 : 3.5;
    
    let imgWidth = '100%';
    let imgHeight = '100%';
    
    if (imageDims) {
        if (imageAspectRatio > cropAspectRatio) {
            // Image is wider than crop container
            imgWidth = `${(imageAspectRatio / cropAspectRatio) * 100}%`;
            imgHeight = '100%';
        } else {
            // Image is taller than crop container
            imgWidth = '100%';
            imgHeight = `${(cropAspectRatio / imageAspectRatio) * 100}%`;
        }
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-ink-overlay/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-ink-card2 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col border border-white/10 transform animate-in zoom-in-95 duration-300">

                {/* Header */}
                <div className="flex justify-between items-center p-5 border-b border-white/10">
                    <div>
                        <h2 className="text-lg font-serif text-ink-fg">Adjust Image</h2>
                        <p className="text-xs font-mono text-ink-fg/40 font-medium uppercase tracking-widest mt-0.5">Drag to pan • Slider to zoom</p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-ink-card3 text-ink-fg/40 hover:text-ink-fg/70 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Viewport Area */}
                <div className="bg-ink-overlay relative h-[350px] sm:h-[450px] flex items-center justify-center overflow-hidden">
                    <div 
                        ref={containerRef}
                        onMouseDown={handleStart}
                        onTouchStart={handleStart}
                        className={`relative cursor-move select-none touch-none flex items-center justify-center ${
                            isCircle 
                            ? 'w-64 h-64 sm:w-80 sm:h-80' 
                            : 'w-[90%] aspect-[3.5/1]'
                        }`}
                    >
                        <img 
                            src={imageUrl} 
                            alt="To Adjust" 
                            className="absolute pointer-events-none select-none max-w-none"
                            style={{ 
                                width: `calc(${imgWidth} * ${zoom})`,
                                height: `calc(${imgHeight} * ${zoom})`,
                                left: `${position.x}%`,
                                top: `${position.y}%`,
                                transform: `translate(-${position.x}%, -${position.y}%)`
                            }}
                        />
                        
                        {/* Guide outline + dimmed mask outside the crop shape, so the preview shows exactly what the real avatar/banner will crop to */}
                        <div
                            className={`absolute inset-0 pointer-events-none border-2 border-white/60 ${
                                isCircle ? 'rounded-full' : 'rounded-2xl'
                            }`}
                            style={{ boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.75), 0 0 15px rgba(0, 0, 0, 0.3)' }}
                        />
                        
                        {/* Subtle center crosshairs */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className="absolute w-4 h-0.5 bg-white/40" />
                            <div className="absolute h-4 w-0.5 bg-white/40" />
                        </div>
                    </div>
                </div>

                {/* Controls Area */}
                <div className="p-6 space-y-6 bg-ink-card2 border-t border-white/10">
                    <div className="space-y-3">
                        <div className="flex justify-between items-center text-[10px] font-mono font-medium text-ink-fg/40 uppercase tracking-widest">
                            <div className="flex items-center gap-1.5"><ZoomIn size={12} /> Zoom</div>
                            <span className="bg-ink-card3 text-ink-fg/70 px-2 py-0.5 rounded-full">{(zoom * 100).toFixed(0)}%</span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="4"
                            step="0.01"
                            value={zoom}
                            onChange={(e) => setZoom(parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-ink-card3 rounded-lg appearance-none cursor-pointer accent-mint"
                        />
                    </div>

                    {/* Live preview at both real header heights — banner crop only */}
                    {!isCircle && imageUrl && (
                        <div className="pt-5 border-t border-white/10">
                            <div className="text-[10px] font-mono font-medium text-ink-fg/30 uppercase tracking-widest mb-3">How it lands</div>
                            <div className="flex gap-2.5">
                                {[
                                    { label: 'CHORES · 138PX', height: 60, title: 'Household' },
                                    { label: 'ANALYTICS · 104PX', height: 46, title: 'Analytics' },
                                ].map(({ label, height, title }) => (
                                    <div key={label} className="flex-1">
                                        <div
                                            className="relative rounded-lg overflow-hidden bg-ink-card3"
                                            style={{ height }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt=""
                                                className="absolute inset-0 w-full h-full object-cover"
                                                style={{
                                                    objectPosition: `${position.x}% ${position.y}%`,
                                                    transform: `scale(${zoom})`,
                                                    transformOrigin: `${position.x}% ${position.y}%`,
                                                }}
                                            />
                                            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ink-overlay/90 via-ink-overlay/10 to-transparent" />
                                            <div className="absolute left-2 bottom-1.5 font-serif text-ink-banner pointer-events-none" style={{ fontSize: height > 50 ? 12 : 11 }}>
                                                {title}
                                            </div>
                                        </div>
                                        <div className="text-[8.5px] font-mono font-medium text-ink-fg/30 uppercase tracking-widest mt-2">{label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-xl text-ink-fg/60 font-bold bg-ink-card3 hover:bg-ink-card transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveClick}
                            className="flex-2 px-8 py-3 bg-mint text-ink-shell rounded-xl hover:brightness-95 font-bold shadow-lg shadow-mint/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                        >
                            <Check size={20} /> Save Adjustment
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageAdjustModal;
